import Cookies from 'js-cookie';
import { NavigateFunction } from 'react-router-dom';
import { action, autorun, computed, makeObservable, observable, runInAction, toJS } from 'mobx';

import { DateUtilsService } from 'shared/utils/date-utils.service';
import { AsyncState } from 'shared/libs/state';
import { PaginationControl } from 'shared/components/pagination';
import {
   ActionPlanDto,
   ActivatePlanResponse,
   AdminPlanIndexResponse,
   DeactivatePlanResponse,
   DeletePlanResponse,
   ManagePlan,
} from './types';
import { PlansManagerApiService } from './PlansManagerApiService';

type SortDirection = 'asc' | 'desc';
type SliderSort = keyof Pick<
   ManagePlan,
   'plan_name' | 'planuser' | 'type_value' | 'amount' | 'created' | 'amount'
>;

export class PlansManagerStore {
   rawPlans: ManagePlan[] = [];
   searchStr: string = '';
   dateFrom = '';
   dateTo = '';
   sorter: SliderSort = 'created';
   direction: SortDirection = 'desc';

   private readonly PER_PAGE = 12;
   readonly pagination = new PaginationControl();

   readonly loadPlansState = new AsyncState<AdminPlanIndexResponse>();
   readonly activateAllState = new AsyncState<AdminPlanIndexResponse>();
   readonly deactivateAllState = new AsyncState<AdminPlanIndexResponse>();
   readonly activatePlanState = new AsyncState<ActivatePlanResponse>();
   readonly deactivatePlanState = new AsyncState<DeactivatePlanResponse>();
   readonly deletePlanState = new AsyncState<DeletePlanResponse>();

   idsArray: number[] = [];

   constructor(private readonly navigate: NavigateFunction) {
      makeObservable(this, {
         rawPlans: observable,
         sorter: observable,
         searchStr: observable,
         dateFrom: observable,
         dateTo: observable,
         direction: observable,
         idsArray: observable,
         plans: computed,
         filteredPlans: computed,
         diasableActivateAll: computed,
         diasableDeactivateAll: computed,
         selectedAll: computed,
         totalPages: computed,
         onSearchChange: action.bound,
         onSelectAll: action.bound,
         onDeselectAll: action.bound,
         onSortBy: action.bound,
      });

      autorun(() => {
         console.log(this.activateAllState.status);
      });
   }

   get totalPages(): number {
      return Math.ceil(this.filteredPlans.length / this.PER_PAGE);
   }

   get filteredPlans(): ManagePlan[] {
      const search = this.searchStr.trim().toLocaleLowerCase();
      const filteredSliders = this.rawPlans.filter((plan) => {
         const matchesSearch = !search || plan.plan_name?.toLocaleLowerCase().includes(search);

         return matchesSearch;
      });

      return filteredSliders.sort(this._getSortCallback());
   }

   get plans(): ManagePlan[] {
      const firstIdx = this.pagination.page * this.PER_PAGE;
      return this.filteredPlans.slice(firstIdx, firstIdx + this.PER_PAGE);
   }

   get diasableActivateAll(): boolean {
      return this.idsArray.length === 0;
   }

   get diasableDeactivateAll(): boolean {
      return this.idsArray.length === 0;
   }

   get selectedAll(): boolean {
      return this.rawPlans.length !== 0 && this.idsArray.length === this.rawPlans.length;
   }

   onSortBy(sort: SliderSort): void {
      if (this.sorter === sort) {
         if (this.direction === 'asc') {
            this.direction = 'desc';
         } else {
            this.direction = 'asc';
         }
      } else {
         this.sorter = sort;
         if (this.direction === 'asc') {
            this.direction = 'desc';
         } else {
            this.direction = 'asc';
         }
      }
   }

   onSearchChange(value: string): void {
      this.searchStr = value;
   }

   onSelectAll(): void {
      this.idsArray = this.rawPlans.map((p) => p.id);
   }

   onDeselectAll(): void {
      this.idsArray = [];
   }

   onToggleById(id: number) {
      if (this.idsArray.includes(id)) {
         this.idsArray = this.idsArray.filter((planId) => planId !== id);
      } else {
         this.idsArray = [...this.idsArray, id];
      }
   }

   private _getSortCallback() {
      if (this.sorter === 'created') {
         return (a: ManagePlan, b: ManagePlan) =>
            DateUtilsService.compare(this.direction, a.created, b.created);
      }

      return (a: ManagePlan, b: ManagePlan) => {
         if (a[this.sorter] < b[this.sorter]) {
            return this.direction === 'asc' ? -1 : 1;
         }
         if (a[this.sorter] > b[this.sorter]) {
            return this.direction === 'asc' ? 1 : -1;
         }
         return 0;
      };
   }

   async loadPlans() {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.loadPlansState.run(
         () =>
            PlansManagerApiService.loadPlans(null, {
               adminID,
               tokenKey,
            }),
         {
            onSuccess: (resp) => {
               runInAction(() => {
                  this.rawPlans = resp.response.map((plan) => ({
                     ...plan,
                     created: DateUtilsService.toAppFormat(plan.created),
                  }));
               });
            },
         }
      );
   }

   async activatePlan({
      dto,
      onSuccess,
      onError,
   }: {
      dto: ActionPlanDto;
      onSuccess?: (res: ActivatePlanResponse) => void;
      onError?: (e: string) => void;
   }) {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.activatePlanState.run(
         () =>
            PlansManagerApiService.activatePlan(dto, {
               adminID,
               tokenKey,
            }),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  onSuccess?.(resp);
                  this.loadPlans();
               } else {
                  onError?.(resp.message);
               }
            },
            onError: (err) => {
               onError?.(err.message);
            },
         }
      );
   }

   async deactivatePlan({
      dto,
      onSuccess,
      onError,
   }: {
      dto: ActionPlanDto;
      onSuccess?: (res: DeactivatePlanResponse) => void;
      onError?: (e: string) => void;
   }) {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.deactivatePlanState.run(
         () =>
            PlansManagerApiService.deactivatePlan(dto, {
               adminID,
               tokenKey,
            }),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  onSuccess?.(resp);
                  this.loadPlans();
               } else {
                  onError?.(resp.message);
               }
            },
            onError: (err) => {
               onError?.(err.message);
            },
         }
      );
   }

   async deletePlan({
      dto,
      onSuccess,
      onError,
   }: {
      dto: ActionPlanDto;
      onSuccess?: (res: DeactivatePlanResponse) => void;
      onError?: (e: string) => void;
   }) {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.deletePlanState.run(
         () =>
            PlansManagerApiService.deletePlan(dto, {
               adminID,
               tokenKey,
            }),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  onSuccess?.(resp);
                  this.loadPlans();
               } else {
                  onError?.(resp.message);
               }
            },
            onError: (err) => {
               onError?.(err.message);
            },
         }
      );
   }

   async deactivateAll({
      onSuccess,
      onError,
   }: {
      onSuccess?: (res: AdminPlanIndexResponse) => void;
      onError?: (e: string) => void;
   }) {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.deactivateAllState.run(
         () =>
            PlansManagerApiService.deactivatedAll(
               {
                  idList: this.idsArray.join(','),
               },
               {
                  adminID,
                  tokenKey,
               }
            ),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  runInAction(() => {
                     this.rawPlans = resp.response.map((plan) => ({
                        ...plan,
                        created: DateUtilsService.toAppFormat(plan.created),
                     }));
                     onSuccess?.(resp);
                  });
               } else {
                  onError?.(resp.message);
               }
            },
            onError: (err) => {
               onError?.(err.message);
            },
         }
      );
   }


   async activateAll({
      onSuccess,
      onError,
   }: {
      onSuccess?: (res: AdminPlanIndexResponse) => void;
      onError?: (e: string) => void;
   }) {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.activateAllState.run(
         () =>
            PlansManagerApiService.activatedAll(
               {
                  idList: this.idsArray.join(','),
               },
               {
                  adminID,
                  tokenKey,
               }
            ),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  runInAction(() => {
                     this.rawPlans = resp.response.map((plan) => ({
                        ...plan,
                        created: DateUtilsService.toAppFormat(plan.created),
                     }));
                     onSuccess?.(resp);
                  });
               } else {
                  onError?.(resp.message);
               }
            },
            onError: (err) => {
               onError?.(err.message);
            },
         }
      );
   }
}
